import { Box, Flex, Image, Text } from '@chakra-ui/react';
import { Badge } from '@foam/buoy/src/components/Badge/Badge';
import React from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  IconButton,
  Tooltip,
  TrashIcon,
} from 'ui-components';
import { LinkIcon } from 'ui-components/dist/LinkIcon';
import { FEATURE_FLAGS, SHORT_DATE_FORMAT } from '~/constants';
import { MEDIA_KIT_TEXTS } from '~/constants/mediaKitTexts';
import { useDeleteMediaKit } from '~/hooks/api/media-kits/delete';
import { MediaKit } from '~/hooks/api/media-kits/get';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { formatDate } from '~/utils';

import { useCopyMKLink } from '../PageBuilder/hooks/useCopyMKLink';
import { useDeleteMediaKitStore } from '../PageBuilder/modals/DeleteConfirmation/store';

const CustomShareableCard = ({
  id,
  name,
  created_at,
  thumbnail,
  contact,
  is_public,
}: MediaKit) => {
  const { openModalToDeleteMK } = useDeleteMediaKitStore();
  const { isEnabled: isPrimaryMediaKitEnabled } = useFeature(
    FEATURE_FLAGS.foamPrimaryMediaKit,
  );
  const { copyMkLink } = useCopyMKLink(id);
  const deleteShareable = useDeleteMediaKit();

  const handleCopyLink: React.MouseEventHandler<HTMLButtonElement> = async (
    evt,
  ) => {
    evt.preventDefault();
    evt.stopPropagation();

    copyMkLink();
  };

  const handleDelete: React.MouseEventHandler<HTMLButtonElement> = async (
    evt,
  ) => {
    evt.preventDefault();
    evt.stopPropagation();

    openModalToDeleteMK(id);
  };

  const tooltipText = React.useMemo(() => {
    if (!is_public) return undefined;
    return isPrimaryMediaKitEnabled
      ? MEDIA_KIT_TEXTS.PRIMARY_DELETE
      : MEDIA_KIT_TEXTS.DEFAULT_DELETE;
  }, [is_public, isPrimaryMediaKitEnabled]);

  return (
    <Card borderRadius={24} data-testid="media-kit-card">
      <Flex direction="column" gap={2}>
        <CardHeader>
          <Box
            height="145px"
            rounded="0.5rem"
            overflow="hidden"
            style={{
              viewTransitionName: `avatar-${id}`,
            }}
            position="relative"
          >
            {is_public ? (
              <div className="absolute right-2 top-2">
                <Tooltip
                  label={
                    isPrimaryMediaKitEnabled
                      ? MEDIA_KIT_TEXTS.PRIMARY_KIT_TOOLTIP
                      : MEDIA_KIT_TEXTS.DEFAULT_KIT_TOOLTIP
                  }
                  placement="top"
                >
                  <Badge
                    data-testid="default-media-kit"
                    size={isPrimaryMediaKitEnabled ? 'sm' : 'md'}
                    variant={isPrimaryMediaKitEnabled ? 'secondary' : 'info'}
                    icon={isPrimaryMediaKitEnabled ? undefined : 'Info'}
                  >
                    {isPrimaryMediaKitEnabled
                      ? MEDIA_KIT_TEXTS.PRIMARY_BADGE_TEXT
                      : MEDIA_KIT_TEXTS.DEFAULT_BADGE_TEXT}
                  </Badge>
                </Tooltip>
              </div>
            ) : null}
            <Image
              src={thumbnail || 'randomstringtothrowerror'}
              data-testid="media-kit-card-thumbnail"
              width="100%"
              height="100%"
              objectFit="cover"
              objectPosition="50% 10%"
              alt={name}
              borderRadius="lg"
              fallbackStrategy="onError"
              fallback={
                <Box
                  display="flex"
                  width="100%"
                  height="100%"
                  borderRadius="lg"
                  justifyContent="center"
                  alignItems="center"
                  backgroundColor="neutral.50"
                  border="1px solid"
                  borderColor="primary.50"
                >
                  <svg
                    width="64"
                    height="64"
                    viewBox="0 0 64 64"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M17.8008 18.0938C17.8008 15.3841 19.9974 13.1875 22.707 13.1875C25.4167 13.1875 27.6133 15.3841 27.6133 18.0938C27.6133 20.8034 25.4167 23 22.707 23C19.9974 23 17.8008 20.8034 17.8008 18.0938ZM22.707 9.1875C17.7882 9.1875 13.8008 13.175 13.8008 18.0938C13.8008 23.0125 17.7882 27 22.707 27C27.6258 27 31.6133 23.0125 31.6133 18.0938C31.6133 13.175 27.6258 9.1875 22.707 9.1875ZM41.6465 19.709C41.6465 17.8914 43.1199 16.418 44.9375 16.418C46.7551 16.418 48.2285 17.8914 48.2285 19.709C48.2285 21.5266 46.7551 23 44.9375 23C43.1199 23 41.6465 21.5266 41.6465 19.709ZM44.9375 12.418C40.9108 12.418 37.6465 15.6823 37.6465 19.709C37.6465 23.7357 40.9108 27 44.9375 27C48.9642 27 52.2285 23.7357 52.2285 19.709C52.2285 15.6823 48.9642 12.418 44.9375 12.418ZM22.7074 35C15.7463 35 10.3438 41.0078 10.3438 48.2969V50.0618C12.8816 50.322 17.8026 50.75 22.7074 50.75C27.6122 50.75 32.5331 50.322 35.071 50.0618V48.2969C35.071 41.0078 29.6684 35 22.7074 35ZM38.855 45.4938C40.6444 45.624 42.7938 45.7344 44.9376 45.7344C48.3006 45.7344 51.6774 45.4627 53.6563 45.2691V44.4113C53.6563 39.2096 49.8112 34.9961 44.9376 34.9961C41.5013 34.9961 38.5763 37.0908 37.1499 40.1497C37.9809 41.8063 38.5629 43.6103 38.855 45.4938ZM34.6761 36.4667C31.7596 33.1424 27.5784 31 22.7074 31C13.2162 31 6.34375 39.1339 6.34375 48.2969V51.5869C6.34375 52.7072 7.16663 53.7283 8.36902 53.8697C9.73651 54.0305 16.2087 54.75 22.7074 54.75C29.206 54.75 35.6783 54.0305 37.0457 53.8697C38.2482 53.7283 39.071 52.7072 39.071 51.5869V49.5189C40.8327 49.6387 42.8828 49.7344 44.9376 49.7344C49.8174 49.7344 54.6708 49.1946 55.6968 49.074C56.8609 48.9371 57.6563 47.9482 57.6563 46.8664V44.4113C57.6563 37.3357 52.3413 30.9961 44.9376 30.9961C40.5689 30.9961 36.9275 33.2034 34.6761 36.4667Z"
                      fill="#3A495F"
                    />
                  </svg>
                </Box>
              }
            />
          </Box>
        </CardHeader>
        <CardBody>
          <Flex direction="column" gap={4}>
            <Flex direction="column" gap={2}>
              <Text
                color="primary.500"
                flex={1}
                fontSize="larger"
                fontWeight={500}
                lineHeight={6}
                noOfLines={1}
                data-testid="media-kit-card-name"
              >
                {name}
              </Text>
              <Flex
                align="center"
                color="primary.400"
                justifyContent="space-between"
                fontSize="md"
                lineHeight={1}
              >
                <Text
                  fontWeight={500}
                  data-testid="media-kit-card-manager-name"
                >
                  {contact.manager_name}
                </Text>
                <Text fontWeight={400} data-testid="media-kit-card-created-at">
                  {formatDate(created_at, SHORT_DATE_FORMAT)}
                </Text>
              </Flex>
            </Flex>

            <Flex gap={2}>
              <Button
                aria-label="copy link"
                leftIcon={<LinkIcon />}
                variant="tertiary"
                onClick={handleCopyLink}
                flex={1}
                trackId="media-kit-card--copy-link-btn"
                data-testid="media-kit-card-copy-link-btn"
              >
                Copy Link
              </Button>
              <Tooltip label={tooltipText} placement="top">
                <span>
                  <IconButton
                    aria-label="delete"
                    icon={<TrashIcon />}
                    variant="tertiary"
                    onClick={handleDelete}
                    isLoading={deleteShareable.isLoading}
                    trackId="media-kit-card--delete-btn"
                    data-testid="media-kit-card-delete-btn"
                    isDisabled={is_public}
                  />
                </span>
              </Tooltip>
            </Flex>
          </Flex>
        </CardBody>
      </Flex>
    </Card>
  );
};

export default CustomShareableCard;
